import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Facebook, Linkedin, Youtube, Instagram, Search, MessageSquare, Menu, X, Flame } from 'lucide-react';
import { useCompany, useContent } from '../context/ContentContext';
import { resolveImageUrl } from './ImageUpload';
import LanguageSwitcher from './LanguageSwitcher';

export default function Header() {
  const companyInfo = useCompany();
  const { sections } = useContent();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const navItems = sections?.nav_items?.length
    ? sections.nav_items.map(n => ({ to: n.path, label: n.label }))
    : [
        { to: '/', label: 'Home' },
        { to: '/about', label: 'About Us' },
        { to: '/products', label: 'Products' },
        { to: '/gallery', label: 'Gallery' },
        { to: '/contact', label: 'Contact Us' },
      ];
  const logoUrl = sections?.header_logo_url || 'https://essbeeheaters.com/admin/uploads/logo/1720678855loo.png';
  const topbarWelcome = sections?.topbar_welcome || 'Welcome to Ess Bee Heaters Pvt. Ltd.';

  return (
    <header className="w-full sticky top-0 z-50 shadow-md">
      {/* Top bar */}
      <div className="topbar">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[13px] overflow-hidden whitespace-nowrap">
            <Flame className="w-4 h-4 text-[var(--brand-orange)]" />
            <span className="hidden sm:inline">{topbarWelcome}</span>
            <span className="sm:hidden">{(companyInfo.name || 'Ess Bee Heaters').slice(0, 30)}</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Custom premium language switcher */}
            <LanguageSwitcher />
            <div className="hidden sm:flex items-center gap-2">
              <a href={companyInfo.socials.facebook} className="w-7 h-7 rounded-full bg-[#2a2a2a] flex items-center justify-center hover:bg-[var(--brand-orange)] transition-colors" aria-label="Facebook"><Facebook className="w-3.5 h-3.5" /></a>
              <a href={companyInfo.socials.linkedin} className="w-7 h-7 rounded-full bg-[#2a2a2a] flex items-center justify-center hover:bg-[var(--brand-orange)] transition-colors" aria-label="LinkedIn"><Linkedin className="w-3.5 h-3.5" /></a>
              <a href={companyInfo.socials.youtube} target="_blank" rel="noreferrer" className="w-7 h-7 rounded-full bg-[#2a2a2a] flex items-center justify-center hover:bg-[var(--brand-orange)] transition-colors" aria-label="YouTube"><Youtube className="w-3.5 h-3.5" /></a>
              <a href={companyInfo.socials.instagram} className="w-7 h-7 rounded-full bg-[#2a2a2a] flex items-center justify-center hover:bg-[var(--brand-orange)] transition-colors" aria-label="Instagram"><Instagram className="w-3.5 h-3.5" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* Logo row */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={resolveImageUrl(logoUrl)}
              alt={companyInfo.name || 'Logo'}
              className="h-16 sm:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-3">
            <CertBadge type="iso" />
            <CertBadge type="rohs" />
            <CertBadge type="ce" />
            <CertBadge type="msme" />
          </div>
        </div>
      </div>

      {/* Nav bar */}
      <div className="relative" style={{ background: 'linear-gradient(90deg, #f7941e 0%, #ffb347 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <nav className="hidden md:flex items-center">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden text-white p-3" aria-label="menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          <div className="flex items-center gap-3">
            <button onClick={() => setSearchOpen(!searchOpen)} className="text-white p-2 hover:bg-white/10 rounded-full transition" aria-label="search">
              <Search className="w-4 h-4" />
            </button>
            <Link to="/contact" className="hidden sm:inline-flex items-center gap-2 bg-white text-[#e67e00] font-display tracking-wide px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#1a1a1a] hover:text-white transition-colors">
              <MessageSquare className="w-4 h-4" /> Instant Message
            </Link>
          </div>
        </div>
        {searchOpen && (
          <div className="bg-white/95 border-t border-white/30 px-4 py-3">
            <div className="max-w-7xl mx-auto">
              <input type="text" placeholder="Search products..." className="w-full px-4 py-2 border border-[#f1e4c8] rounded focus:outline-none focus:ring-2 focus:ring-[#f7941e]" />
            </div>
          </div>
        )}
        {menuOpen && (
          <nav className="md:hidden bg-white border-t">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setMenuOpen(false)} className={({ isActive }) => `block px-4 py-3 border-b border-gray-100 font-display uppercase text-sm ${isActive ? 'text-[#f7941e]' : 'text-gray-800'}`}>
                {item.label}
              </NavLink>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}

function CertBadge({ type }) {
  const badges = {
    iso: {
      title: 'ISO 9001:2015',
      sub: 'Certified',
      svg: (
        <svg viewBox="0 0 40 40" className="w-9 h-9">
          <defs>
            <linearGradient id="isoG" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1e40af" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="url(#isoG)" />
          <circle cx="20" cy="20" r="14" fill="none" stroke="#fff" strokeWidth="0.8" opacity="0.6" />
          <text x="20" y="17" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="700" fontFamily="Arial">ISO</text>
          <text x="20" y="26" textAnchor="middle" fill="#fff" fontSize="6" fontWeight="600" fontFamily="Arial">9001</text>
          <text x="20" y="32" textAnchor="middle" fill="#fff" fontSize="4.5" fontFamily="Arial">2015</text>
        </svg>
      ),
    },
    rohs: {
      title: 'RoHS',
      sub: 'Compliant',
      svg: (
        <svg viewBox="0 0 40 40" className="w-9 h-9">
          <defs>
            <linearGradient id="rohsG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a34a" />
              <stop offset="100%" stopColor="#15803d" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="url(#rohsG)" />
          <path d="M20 9 C 14 14 13 19 17 24 C 13 24 11 21 11 18 C 11 25 16 30 20 30 C 24 30 29 25 29 18 C 29 21 27 24 23 24 C 27 19 26 14 20 9 Z" fill="#fff" />
        </svg>
      ),
    },
    ce: {
      title: 'CE',
      sub: 'Marked',
      svg: (
        <svg viewBox="0 0 40 40" className="w-9 h-9">
          <defs>
            <linearGradient id="ceG" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#1f2937" />
              <stop offset="100%" stopColor="#4b5563" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="url(#ceG)" />
          <path d="M16 12 A 8 8 0 1 0 16 28" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
          <path d="M22 12 A 8 8 0 1 0 22 28" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
          <line x1="20" y1="20" x2="26" y2="20" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
        </svg>
      ),
    },
    msme: {
      title: 'MSME',
      sub: 'Registered',
      svg: (
        <svg viewBox="0 0 40 40" className="w-9 h-9">
          <defs>
            <linearGradient id="msmeG" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f7941e" />
              <stop offset="100%" stopColor="#e67e00" />
            </linearGradient>
          </defs>
          <circle cx="20" cy="20" r="18" fill="url(#msmeG)" />
          <path d="M11 28 L 14 16 L 18 24 L 22 16 L 26 24 L 29 16 L 29 28 Z" fill="#fff" />
        </svg>
      ),
    },
  };
  const b = badges[type];
  return (
    <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 shadow-sm bg-white hover:shadow-md transition-shadow">
      {b.svg}
      <div className="leading-tight">
        <p className="text-[11px] font-bold text-gray-800 whitespace-nowrap">{b.title}</p>
        <p className="text-[9px] text-gray-500 uppercase tracking-wider">{b.sub}</p>
      </div>
    </div>
  );
}
